/**
 * Hand-written types for the `wanderfit-api` Founding Partners admin surface
 * (`founding-admin.controller.ts`, ADR-0010). Hand-written rather than from the
 * Orval client — see CLAUDE.md → TD-07. Shapes mirror the controller/use-case
 * projections.
 */

import type { Role } from "@/shared/types/role";

export type FoundingProgram = "FOUNDING_HOST" | "PORTFOLIO_CUSTOM";
export type FoundingPhase = "INTRO" | "ONGOING" | "FLAT" | "LAPSED";

/**
 * The configured founding-discount defaults (`GET /admin/founding/rates`,
 * ADR-0010 §9). Pre-fill the assign dialog's intro/ongoing inputs so the admin
 * sees — and can override — the same rates the server would otherwise apply
 * silently. Roles ADMIN + FINANCE.
 */
export interface FoundingRates {
  readonly introPercent: number;
  readonly ongoingPercent: number;
}

/** The enrollment status embedded in each report row (or a mutation response). */
export interface FoundingEnrollment {
  readonly program: FoundingProgram;
  readonly phase: FoundingPhase;
  readonly introPercent: number | null;
  readonly ongoingPercent: number | null;
  readonly flatPercent: number | null;
  readonly appliedStripeCouponId: string | null;
  readonly unassignedAt: string | null;
  readonly lapsedAt: string | null;
}

/**
 * The full founding enrollment for a single property
 * (`GET /admin/founding/enrollments/:propertyId`). Extends the compact
 * report-row shape with the provenance / timeline fields the property-detail
 * discount view needs. Roles ADMIN + FINANCE.
 */
export interface FoundingEnrollmentDetail extends FoundingEnrollment {
  readonly introEndsAt: string | null;
  readonly grantedByUserId: string | null;
  /**
   * Display name of the admin who granted the enrollment (`grantedByUserId`),
   * resolved server-side. `null` when the granter can't be resolved (deleted
   * admin / lookup failure) — best-effort, never blocks the read.
   */
  readonly grantedByName: string | null;
  readonly grantedAt: string | null;
}

/** One property row of the founding report (`GET /admin/founding/report`). */
export interface FoundingReportRow {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly country: string;
  readonly createdAt: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly currentTier: string | null;
  readonly founding: FoundingEnrollment;
}

export interface FoundingAccountSummary {
  readonly ownerId: string;
  readonly ownerName: string;
  readonly propertyCount: number;
  readonly activeFounderCount: number;
  readonly tierMix: Readonly<Record<string, number>>;
}

export interface FoundingReportResult {
  readonly properties: readonly FoundingReportRow[];
  readonly accounts: readonly FoundingAccountSummary[];
  readonly truncated: boolean;
}

export interface FoundingReportFilters {
  readonly country: string;
  readonly tier: string;
}

/**
 * Account-level Founding Partner status (ADR-0010 §9), returned by
 * `GET /admin/founding/accounts/:userId`. The `foundingPartner` badge flag is
 * uniform across roles; the grandfather/property fields are only meaningful
 * for hosts (a venue owner's status is badge-only).
 */
export interface FoundingAccountStatus {
  readonly userId: string;
  readonly role: Role;
  readonly foundingPartner: boolean;
  readonly grandfatherNewProperties: boolean;
  readonly foundingPropertyCount: number;
  readonly activeFoundingPropertyCount: number;
}

/**
 * One row of a host's property list
 * (`GET /admin/founding/accounts/:userId/properties`). Unlike the founder
 * cockpit report, this list INCLUDES non-founding properties — `founding` is
 * null for a property that has never been enrolled.
 */
export interface OwnerPropertyRow {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly status: string;
  readonly currentTier: string | null;
  readonly founding: FoundingEnrollment | null;
}

export interface OwnerPropertiesResult {
  readonly rows: readonly OwnerPropertyRow[];
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

export interface BulkFoundingFailure {
  readonly propertyId: string;
  readonly errorCode: string;
  readonly message: string;
}

export interface BulkFoundingResult {
  readonly succeeded: readonly string[];
  readonly failed: readonly BulkFoundingFailure[];
}

/**
 * The effective status of a founding enrollment, derived for display. Order
 * matters: lapsed (permanent) beats unassigned beats the live phases.
 */
export type FoundingStatus = "LAPSED" | "UNASSIGNED" | "INTRO" | "ONGOING" | "FLAT";

export function deriveFoundingStatus(e: FoundingEnrollment): FoundingStatus {
  if (e.lapsedAt) return "LAPSED";
  if (e.unassignedAt) return "UNASSIGNED";
  return e.phase as FoundingStatus;
}

/** The discount percent currently in force for display (null if none). */
export function currentPercent(e: FoundingEnrollment): number | null {
  switch (e.phase) {
    case "INTRO":
      return e.introPercent;
    case "ONGOING":
      return e.ongoingPercent;
    case "FLAT":
      return e.flatPercent;
    default:
      return null;
  }
}
